import { format } from 'date-fns';
import type { Appointment } from '@/api/appointments/appointmentsApi.types';
import type { ExternalCalendarEvent } from '@/api/calendarSync/calendarSyncApi.types';
import { parseTimeToMinutes } from './appointmentLayout';

const CANCELLED_STATUS_CODE = 'cancelled';
const END_OF_DAY_TIME = '23:59';

export interface ExternalEventDaySpan {
  id: string;
  title: string;
  isoDate: string;
  startTime: string;
  endTime: string;
}

export interface CalendarClash {
  kind: 'appointment' | 'external';
  label: string;
  startTime: string;
  endTime: string;
}

export function deriveExternalEventDaySpan(
  externalEvent: ExternalCalendarEvent
): ExternalEventDaySpan | null {
  if (externalEvent.allDay === true) {
    return null;
  }
  const startDate = new Date(externalEvent.start);
  const endDate = new Date(externalEvent.end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }
  const startIsoDate = format(startDate, 'yyyy-MM-dd');
  const endIsoDate = format(endDate, 'yyyy-MM-dd');
  const endTime = startIsoDate === endIsoDate ? format(endDate, 'HH:mm') : END_OF_DAY_TIME;
  return {
    id: externalEvent.id,
    title: externalEvent.title,
    isoDate: startIsoDate,
    startTime: format(startDate, 'HH:mm'),
    endTime,
  };
}

export function filterExternalEventDaySpansForIsoDate(
  externalEvents: ExternalCalendarEvent[],
  isoDate: string
): ExternalEventDaySpan[] {
  const spans: ExternalEventDaySpan[] = [];
  for (const externalEvent of externalEvents) {
    const span = deriveExternalEventDaySpan(externalEvent);
    if (span !== null && span.isoDate === isoDate) {
      spans.push(span);
    }
  }
  return spans;
}

function doTimeRangesOverlap(
  firstStartTime: string,
  firstEndTime: string,
  secondStartTime: string,
  secondEndTime: string
): boolean {
  const firstStart = parseTimeToMinutes(firstStartTime);
  const firstEnd = parseTimeToMinutes(firstEndTime);
  const secondStart = parseTimeToMinutes(secondStartTime);
  const secondEnd = parseTimeToMinutes(secondEndTime);
  return firstStart < secondEnd && secondStart < firstEnd;
}

interface DraftTimeRange {
  date: string;
  startTime: string;
  endTime: string;
}

export function computeDraftClashes(
  draft: DraftTimeRange,
  appointments: Appointment[],
  externalEvents: ExternalCalendarEvent[],
  excludeAppointmentId: string | null
): CalendarClash[] {
  if (draft.date.length === 0 || draft.startTime.length === 0 || draft.endTime.length === 0) {
    return [];
  }
  if (parseTimeToMinutes(draft.endTime) <= parseTimeToMinutes(draft.startTime)) {
    return [];
  }
  const clashes: CalendarClash[] = [];
  for (const appointment of appointments) {
    if (appointment.date !== draft.date) {
      continue;
    }
    if (excludeAppointmentId !== null && appointment.id === excludeAppointmentId) {
      continue;
    }
    if (appointment.status === CANCELLED_STATUS_CODE) {
      continue;
    }
    if (
      doTimeRangesOverlap(
        draft.startTime,
        draft.endTime,
        appointment.startTime,
        appointment.endTime
      ) === true
    ) {
      clashes.push({
        kind: 'appointment',
        label: appointment.patientName,
        startTime: appointment.startTime,
        endTime: appointment.endTime,
      });
    }
  }
  for (const span of filterExternalEventDaySpansForIsoDate(externalEvents, draft.date)) {
    if (
      doTimeRangesOverlap(draft.startTime, draft.endTime, span.startTime, span.endTime) === true
    ) {
      clashes.push({
        kind: 'external',
        label: span.title,
        startTime: span.startTime,
        endTime: span.endTime,
      });
    }
  }
  return clashes;
}
